import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tag, Printer, Search, Eye, Loader, X, RefreshCw, ChevronLeft, ChevronRight, Save, RotateCcw, Settings, Move, Image, Plus, Trash2, Copy } from 'lucide-react';
import apiService from '../../services/apiService';
import { useApp } from '../../context/AppContext';
import './EtiquetasZebraPage.css';

const CONFIG_KEY = 'etiquetas_zebra_config';
const PERFILES_KEY = 'etiquetas_zebra_perfiles';

const DEFAULT_CONFIG = {
  impresora: '',
  dpi: 203,
  anchoMm: 50,
  altoMm: 25,
  // Elementos con posicion libre (x, y en mm)
  elementos: {
    logo: { x: 0, y: 0, visible: false, anchoMm: 10, altoMm: 10 },
    nombre: { x: 1, y: 1, visible: true },
    codigoBarras: { x: 5, y: 7, visible: true },
    codigoTexto: { x: 10, y: 17, visible: true },
    precio: { x: 10, y: 20, visible: true },
  },
  // Logo (base64)
  logoImage: '',
  // Fuentes (tamano en dots)
  nombreFontSize: 24,
  nombreMaxChars: 30,
  precioFontSize: 32,
  precioPrefix: '$',
  codigoTextoFontSize: 18,
  // Codigo de barras
  barcodeHeight: 60,
  barcodeModuleWidth: 2,
  barcodeType: 'CODE128',
  // Precio
  precioMostrar: 'precio1',
  copias: 1,
  velocidad: 4,
  oscuridad: 15,
};

// Input numerico que permite escribir libremente y valida al salir
const NumInput = ({ value, onChange, min = 0, max = 999, step, isFloat, ...props }) => {
  const [localVal, setLocalVal] = useState(String(value));
  const inputRef = useRef(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setLocalVal(String(value));
    }
  }, [value]);

  const handleBlur = () => {
    const parsed = isFloat ? parseFloat(localVal) : parseInt(localVal, 10);
    if (isNaN(parsed)) {
      setLocalVal(String(value));
      return;
    }
    const clamped = Math.max(min, Math.min(max, parsed));
    setLocalVal(String(clamped));
    onChange(clamped);
  };

  return (
    <input
      ref={inputRef}
      type="number"
      value={localVal}
      min={min}
      max={max}
      step={step}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      {...props}
    />
  );
};

const ELEM_LABELS = { logo: 'Logo', nombre: 'Nombre', codigoBarras: 'Cod. barras', codigoTexto: 'Texto codigo', precio: 'Precio' };
const ELEM_COLORS = { logo: '#f59e0b', nombre: '#3b82f6', codigoBarras: '#8b5cf6', codigoTexto: '#06b6d4', precio: '#10b981' };

// Convertir imagen a monocromo para ZPL (^GFA)
const imageToZplGrf = (imgSrc, widthDots, heightDots) => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = widthDots;
      canvas.height = heightDots;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, widthDots, heightDots);
      ctx.drawImage(img, 0, 0, widthDots, heightDots);
      const imageData = ctx.getImageData(0, 0, widthDots, heightDots);
      const pixels = imageData.data;

      const bytesPerRow = Math.ceil(widthDots / 8);
      const totalBytes = bytesPerRow * heightDots;
      let hex = '';

      for (let y = 0; y < heightDots; y++) {
        for (let byteIdx = 0; byteIdx < bytesPerRow; byteIdx++) {
          let byte = 0;
          for (let bit = 0; bit < 8; bit++) {
            const x = byteIdx * 8 + bit;
            if (x < widthDots) {
              const idx = (y * widthDots + x) * 4;
              const gray = (pixels[idx] * 0.299 + pixels[idx + 1] * 0.587 + pixels[idx + 2] * 0.442);
              if (gray < 128) {
                byte |= (1 << (7 - bit));
              }
            }
          }
          hex += byte.toString(16).padStart(2, '0').toUpperCase();
        }
      }
      resolve({ hex, bytesPerRow, totalBytes });
    };
    img.onerror = () => resolve(null);
    img.src = imgSrc;
  });
};

const EtiquetasZebraPage = () => {
  const { showToast } = useApp();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalProductos, setTotalProductos] = useState(0);
  const [seleccionados, setSeleccionados] = useState({});
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
  const [configCargada, setConfigCargada] = useState(false);
  const [impresoras, setImpresoras] = useState([]);
  const [loadingImpresoras, setLoadingImpresoras] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [configGuardada, setConfigGuardada] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [elemSeleccionado, setElemSeleccionado] = useState(null);
  const [showGrid, setShowGrid] = useState(true);
  const [perfiles, setPerfiles] = useState([]);
  const [perfilActivo, setPerfilActivo] = useState('');
  const [nombreNuevoPerfil, setNombreNuevoPerfil] = useState('');
  const [mostrarNuevoPerfil, setMostrarNuevoPerfil] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const searchTimeout = useRef(null);
  const labelRef = useRef(null);
  const elementDrag = useRef(null);
  const sizeDrag = useRef(null);
  const logoInputRef = useRef(null);

  const mmToDots = (mm) => Math.round(mm * (config.dpi / 25.4));

  // --- Logo upload ---
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Solo se permiten archivos de imagen', 'warning');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Resize para que no sea muy grande en la BD
      const img = new window.Image();
      img.onload = () => {
        const maxSize = 200;
        let w = img.width, h = img.height;
        if (w > maxSize || h > maxSize) {
          if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
          else { w = Math.round(w * maxSize / h); h = maxSize; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        // Convertir a blanco y negro puro (1-bit)
        const imageData = ctx.getImageData(0, 0, w, h);
        const pixels = imageData.data;
        for (let i = 0; i < pixels.length; i += 4) {
          const gray = pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114;
          const bw = gray < 128 ? 0 : 255;
          pixels[i] = bw;
          pixels[i + 1] = bw;
          pixels[i + 2] = bw;
          pixels[i + 3] = 255;
        }
        ctx.putImageData(imageData, 0, 0);
        const base64 = canvas.toDataURL('image/png');
        updateConfig({ logoImage: base64 });
        updateElemento('logo', { visible: true });
        showToast('Logo cargado', 'success');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const quitarLogo = () => {
    updateConfig({ logoImage: '' });
    updateElemento('logo', { visible: false });
  };

  // --- Config BD ---
  const parseSavedConfig = (saved) => {
    if (saved.orden && !saved.elementos) {
      saved.elementos = { ...DEFAULT_CONFIG.elementos };
    }
    return { ...DEFAULT_CONFIG, ...saved, elementos: { ...DEFAULT_CONFIG.elementos, ...(saved.elementos || {}) } };
  };

  const cargarConfig = async () => {
    try {
      // Cargar perfiles
      const perfilesResult = await apiService.getConfiguracion(PERFILES_KEY);
      if (perfilesResult.success && perfilesResult.data && perfilesResult.data.valor) {
        const savedPerfiles = typeof perfilesResult.data.valor === 'string'
          ? JSON.parse(perfilesResult.data.valor)
          : perfilesResult.data.valor;
        if (Array.isArray(savedPerfiles)) setPerfiles(savedPerfiles);
      }

      // Cargar config activa
      const result = await apiService.getConfiguracion(CONFIG_KEY);
      if (result.success && result.data && result.data.valor) {
        const saved = typeof result.data.valor === 'string'
          ? JSON.parse(result.data.valor)
          : result.data.valor;
        setConfig(parseSavedConfig(saved));
        if (saved._perfilId) setPerfilActivo(saved._perfilId);
      }
    } catch (e) { /* usa defaults */ }
    setConfigCargada(true);
  };

  const guardarConfigEnBD = async (configToSave) => {
    try {
      await apiService.actualizarConfiguraciones([
        { clave: CONFIG_KEY, valor: configToSave }
      ]);
      return true;
    } catch (e) {
      console.error('Error guardando config etiquetas:', e);
      return false;
    }
  };

  const guardarPerfilesEnBD = async (perfilesData) => {
    try {
      await apiService.actualizarConfiguraciones([
        { clave: PERFILES_KEY, valor: perfilesData }
      ]);
      return true;
    } catch (e) {
      console.error('Error guardando perfiles:', e);
      return false;
    }
  };

  const updateConfig = (updates) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const updateElemento = (key, updates) => {
    setConfig(prev => ({
      ...prev,
      elementos: {
        ...prev.elementos,
        [key]: { ...prev.elementos[key], ...updates }
      }
    }));
  };

  const guardarConfigManual = async () => {
    setGuardando(true);
    const configConPerfil = { ...config, _perfilId: perfilActivo };
    const ok = await guardarConfigEnBD(configConPerfil);
    // Si hay perfil activo, actualizar tambien en la lista de perfiles
    if (ok && perfilActivo) {
      const nuevosPerfiles = perfiles.map(p =>
        p.id === perfilActivo ? { ...p, config: configConPerfil } : p
      );
      setPerfiles(nuevosPerfiles);
      await guardarPerfilesEnBD(nuevosPerfiles);
    }
    setGuardando(false);
    if (ok) {
      setConfigGuardada(true);
      setTimeout(() => setConfigGuardada(false), 2000);
      showToast('Configuracion guardada correctamente', 'success');
    } else {
      showToast('Error al guardar configuracion', 'error');
    }
  };

  const resetConfig = async () => {
    const impresora = config.impresora;
    const newConfig = { ...DEFAULT_CONFIG, impresora };
    setConfig(newConfig);
    setPerfilActivo('');
    await guardarConfigEnBD(newConfig);
    showToast('Configuracion restaurada', 'success');
  };

  // --- Perfiles ---
  const crearPerfil = async () => {
    const nombre = nombreNuevoPerfil.trim();
    if (!nombre) { showToast('Ingresa un nombre para el perfil', 'warning'); return; }
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const configConPerfil = { ...config, _perfilId: id };
    const nuevoPerfil = { id, nombre, config: configConPerfil };
    const nuevosPerfiles = [...perfiles, nuevoPerfil];
    setPerfiles(nuevosPerfiles);
    setPerfilActivo(id);
    setNombreNuevoPerfil('');
    setMostrarNuevoPerfil(false);
    await guardarPerfilesEnBD(nuevosPerfiles);
    await guardarConfigEnBD(configConPerfil);
    showToast(`Perfil "${nombre}" creado`, 'success');
  };

  const cargarPerfil = async (id) => {
    const perfil = perfiles.find(p => p.id === id);
    if (!perfil) return;
    const loaded = parseSavedConfig(perfil.config || {});
    setConfig(loaded);
    setPerfilActivo(id);
    await guardarConfigEnBD({ ...loaded, _perfilId: id });
    showToast(`Perfil "${perfil.nombre}" cargado`, 'success');
  };

  const duplicarPerfil = async (id) => {
    const perfil = perfiles.find(p => p.id === id);
    if (!perfil) return;
    const nuevoId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const nuevoPerfil = { id: nuevoId, nombre: `${perfil.nombre} (copia)`, config: { ...perfil.config, _perfilId: nuevoId } };
    const nuevosPerfiles = [...perfiles, nuevoPerfil];
    setPerfiles(nuevosPerfiles);
    await guardarPerfilesEnBD(nuevosPerfiles);
    showToast(`Perfil duplicado`, 'success');
  };

  const eliminarPerfil = async (id) => {
    const perfil = perfiles.find(p => p.id === id);
    if (!perfil) return;
    const nuevosPerfiles = perfiles.filter(p => p.id !== id);
    setPerfiles(nuevosPerfiles);
    if (perfilActivo === id) setPerfilActivo('');
    await guardarPerfilesEnBD(nuevosPerfiles);
    showToast(`Perfil "${perfil.nombre}" eliminado`, 'success');
  };

  useEffect(() => {
    const cargarCategorias = async () => {
      try {
        const result = await apiService.getCategorias();
        if (result.success && result.data) {
          setCategorias(Array.isArray(result.data) ? result.data : []);
        }
      } catch (e) { /* silencioso */ }
    };
    cargarCategorias();
    cargarImpresoras();
    cargarConfig();
  }, []);

  const cargarProductos = useCallback(async (pag = 1) => {
    setLoading(true);
    try {
      const result = await apiService.request('POST', {
        tipo: 'getProductosEtiquetas',
        busqueda,
        categoria_id: categoriaFiltro || null,
        pagina: pag,
        limite: 50
      });
      if (result.success && result.data) {
        setProductos(result.data.productos || []);
        setTotalPaginas(result.data.total_paginas || 1);
        setTotalProductos(result.data.total || 0);
        setPagina(pag);
      }
    } catch (e) {
      console.error('Error cargando productos:', e);
    } finally {
      setLoading(false);
    }
  }, [busqueda, categoriaFiltro]);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      cargarProductos(1);
    }, 300);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [cargarProductos]);

  const cargarImpresoras = async () => {
    if (!window.electron?.ipcRenderer) return;
    setLoadingImpresoras(true);
    try {
      const result = await window.electron.ipcRenderer.invoke('get-all-printers');
      if (result.success && result.printers) {
        setImpresoras(result.printers);
      } else if (Array.isArray(result)) {
        setImpresoras(result);
      }
    } catch (e) {
      console.error('Error cargando impresoras:', e);
    } finally {
      setLoadingImpresoras(false);
    }
  };

  const toggleProducto = (producto) => {
    setSeleccionados(prev => {
      const copy = { ...prev };
      if (copy[producto.id]) {
        delete copy[producto.id];
      } else {
        copy[producto.id] = { ...producto, cantidad: 1 };
      }
      return copy;
    });
  };

  const cambiarCantidad = (productoId, cantidad) => {
    const qty = Math.max(1, Math.min(999, parseInt(cantidad) || 1));
    setSeleccionados(prev => ({
      ...prev,
      [productoId]: { ...prev[productoId], cantidad: qty }
    }));
  };

  const quitarSeleccion = (productoId) => {
    setSeleccionados(prev => {
      const copy = { ...prev };
      delete copy[productoId];
      return copy;
    });
  };

  // Overrides por producto (tamaños de fuente individuales)
  const updateProductoOverride = (productoId, key, value) => {
    setSeleccionados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        overrides: { ...(prev[productoId]?.overrides || {}), [key]: value }
      }
    }));
  };

  const resetProductoOverrides = (productoId) => {
    setSeleccionados(prev => ({
      ...prev,
      [productoId]: { ...prev[productoId], overrides: undefined }
    }));
  };

  // Obtener valor efectivo: override del producto o valor global
  const getVal = (producto, key) => {
    return producto?.overrides?.[key] ?? config[key];
  };

  const toggleTodos = () => {
    const todosSeleccionados = productos.every(p => seleccionados[p.id]);
    if (todosSeleccionados) {
      setSeleccionados(prev => {
        const copy = { ...prev };
        productos.forEach(p => delete copy[p.id]);
        return copy;
      });
    } else {
      setSeleccionados(prev => {
        const copy = { ...prev };
        productos.forEach(p => {
          if (!copy[p.id]) {
            copy[p.id] = { ...p, cantidad: 1 };
          }
        });
        return copy;
      });
    }
  };

  // --- Generar ZPL con posiciones absolutas ---
  const generarZPL = async (producto, cantidadEtiquetas) => {
    const anchoDots = mmToDots(config.anchoMm);
    const altoDots = mmToDots(config.altoMm);
    const elems = config.elementos;

    const precio = producto[config.precioMostrar] || producto.precio1 || 0;
    const codigoBarras = producto.codigo_barras || producto.codigo_corto || '';
    const nombre = (producto.nombre || '').substring(0, getVal(producto, 'nombreMaxChars'));

    // Tamaños por producto (override o global)
    const pNombreFs = getVal(producto, 'nombreFontSize');
    const pPrecioFs = getVal(producto, 'precioFontSize');
    const pCodigoTextoFs = getVal(producto, 'codigoTextoFontSize');
    const pBarcodeHeight = getVal(producto, 'barcodeHeight');
    const pBarcodeModuleWidth = getVal(producto, 'barcodeModuleWidth');

    const barcodeCmd = () => {
      switch (config.barcodeType) {
        case 'EAN13': return 'BE';
        case 'CODE39': return 'B3';
        default: return 'BC';
      }
    };

    // Pre-convertir logo si es necesario
    let logoGrf = null;
    if (elems.logo?.visible && config.logoImage) {
      const logoWDots = mmToDots(elems.logo.anchoMm || 10);
      const logoHDots = mmToDots(elems.logo.altoMm || 10);
      logoGrf = await imageToZplGrf(config.logoImage, logoWDots, logoHDots);
    }

    let zpl = '';
    for (let i = 0; i < cantidadEtiquetas; i++) {
      zpl += '^XA\n';
      zpl += `^PW${anchoDots}\n`;
      zpl += `^LL${altoDots}\n`;
      zpl += '^CI28\n';
      if (config.velocidad) zpl += `^PR${config.velocidad}\n`;
      if (config.oscuridad) zpl += `~SD${config.oscuridad}\n`;

      // Logo
      if (logoGrf && elems.logo?.visible) {
        const x = mmToDots(elems.logo.x);
        const y = mmToDots(elems.logo.y);
        zpl += `^FO${x},${y}^GFA,${logoGrf.totalBytes},${logoGrf.totalBytes},${logoGrf.bytesPerRow},${logoGrf.hex}^FS\n`;
      }

      // Nombre
      if (elems.nombre?.visible) {
        const x = mmToDots(elems.nombre.x);
        const y = mmToDots(elems.nombre.y);
        zpl += `^FO${x},${y}^A0N,${pNombreFs},${pNombreFs}^FD${nombre}^FS\n`;
      }

      // Codigo de barras
      if (elems.codigoBarras?.visible && codigoBarras) {
        const x = mmToDots(elems.codigoBarras.x);
        const y = mmToDots(elems.codigoBarras.y);
        zpl += `^FO${x},${y}^BY${pBarcodeModuleWidth},2,${pBarcodeHeight}^${barcodeCmd()}N,${pBarcodeHeight},N,N,N^FD${codigoBarras}^FS\n`;
      }

      // Codigo texto
      if (elems.codigoTexto?.visible && codigoBarras) {
        const x = mmToDots(elems.codigoTexto.x);
        const y = mmToDots(elems.codigoTexto.y);
        zpl += `^FO${x},${y}^A0N,${pCodigoTextoFs},${pCodigoTextoFs}^FD${codigoBarras}^FS\n`;
      }

      // Precio (alineado a la derecha: x marca el borde derecho)
      if (elems.precio?.visible) {
        const x = mmToDots(elems.precio.x);
        const y = mmToDots(elems.precio.y);
        const precioFormateado = `${config.precioPrefix}${parseFloat(precio).toFixed(2)}`;
        zpl += `^FO0,${y}^A0N,${pPrecioFs},${pPrecioFs}^FB${x},1,0,R,0^FD${precioFormateado}^FS\n`;
      }

      zpl += '^XZ\n';
    }
    return zpl;
  };

  const imprimirEtiquetas = async () => {
    const listaImprimir = Object.values(seleccionados);
    if (listaImprimir.length === 0) {
      showToast('Selecciona al menos un producto', 'warning');
      return;
    }
    if (!config.impresora) {
      showToast('Selecciona una impresora Zebra', 'warning');
      return;
    }

    setImprimiendo(true);
    try {
      let zplCompleto = '';
      for (const producto of listaImprimir) {
        const copiasTotales = producto.cantidad * (config.copias || 1);
        zplCompleto += await generarZPL(producto, copiasTotales);
      }

      if (window.electron?.ipcRenderer) {
        const result = await window.electron.ipcRenderer.invoke('print-raw-data', {
          printerName: config.impresora,
          rawData: zplCompleto,
        });

        if (result.success) {
          showToast(`Etiquetas enviadas a ${config.impresora} correctamente`, 'success');
        } else {
          showToast('Error al imprimir: ' + (result.message || result.error || 'Error desconocido'), 'error');
        }
      } else {
        try {
          await navigator.clipboard.writeText(zplCompleto);
          showToast('ZPL copiado al portapapeles', 'success');
        } catch (e) {
          console.log('ZPL generado:', zplCompleto);
          showToast('No se pudo copiar. Revisa la consola para el codigo ZPL.', 'error');
        }
      }
    } catch (error) {
      console.error('Error imprimiendo:', error);
      showToast('Error al imprimir: ' + error.message, 'error');
    } finally {
      setImprimiendo(false);
    }
  };

  // --- Canvas: drag libre de elementos ---
  const handleElementMouseDown = (e, elemKey) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setElemSeleccionado(elemKey);
    const rect = labelRef.current.getBoundingClientRect();
    elementDrag.current = {
      elemKey,
      startX: e.clientX,
      startY: e.clientY,
      startElemX: config.elementos[elemKey].x,
      startElemY: config.elementos[elemKey].y,
      rect,
    };

    const handleMouseMove = (ev) => {
      if (!elementDrag.current) return;
      const { startX, startY, startElemX, startElemY } = elementDrag.current;
      const deltaMmX = (ev.clientX - startX) / previewScale;
      const deltaMmY = (ev.clientY - startY) / previewScale;
      const snapMm = 0.5;
      const newX = Math.max(0, Math.min(config.anchoMm - 2, Math.round((startElemX + deltaMmX) / snapMm) * snapMm));
      const newY = Math.max(0, Math.min(config.altoMm - 2, Math.round((startElemY + deltaMmY) / snapMm) * snapMm));
      updateElemento(elementDrag.current.elemKey, { x: newX, y: newY });
    };

    const handleMouseUp = () => {
      elementDrag.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // --- Canvas: resize de tamaño arrastrando borde inferior ---
  const sizeConfigMap = {
    nombre: { key: 'nombreFontSize', min: 10, max: 999 },
    codigoBarras: { key: 'barcodeHeight', min: 20, max: 999 },
    codigoTexto: { key: 'codigoTextoFontSize', min: 8, max: 999 },
    precio: { key: 'precioFontSize', min: 12, max: 999 },
  };

  const handleSizeMouseDown = (e, elemKey) => {
    e.preventDefault();
    e.stopPropagation();
    const mapping = sizeConfigMap[elemKey];
    if (!mapping) return;
    sizeDrag.current = {
      elemKey,
      startY: e.clientY,
      startValue: config[mapping.key],
      mapping,
    };

    const handleMouseMove = (ev) => {
      if (!sizeDrag.current) return;
      const { startY, startValue, mapping: m } = sizeDrag.current;
      const deltaDots = Math.round((ev.clientY - startY) / 2);
      const newVal = Math.max(m.min, Math.min(m.max, startValue + deltaDots));
      updateConfig({ [m.key]: newVal });
    };

    const handleMouseUp = () => {
      sizeDrag.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // --- Canvas: resize horizontal barcode (barcodeModuleWidth) ---
  const handleBarcodeWidthMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startValue = config.barcodeModuleWidth;

    const handleMouseMove = (ev) => {
      const deltaPx = ev.clientX - startX;
      const delta = Math.round(deltaPx / 8);
      const newVal = Math.max(1, Math.min(10, startValue + delta));
      updateConfig({ barcodeModuleWidth: newVal });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // --- Canvas: resize logo (ancho y alto en mm, proporcional) ---
  const handleLogoResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startH = config.elementos.logo?.altoMm || 10;
    const startW = config.elementos.logo?.anchoMm || 10;
    const ratio = startW / startH;

    const handleMouseMove = (ev) => {
      const deltaMm = (ev.clientY - startY) / previewScale;
      const newH = Math.max(2, Math.min(config.altoMm, Math.round((startH + deltaMm) * 2) / 2));
      const newW = Math.max(2, Math.min(config.anchoMm, Math.round((newH * ratio) * 2) / 2));
      updateElemento('logo', { altoMm: newH, anchoMm: newW });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const seleccionadosList = Object.values(seleccionados);
  const totalSeleccionados = seleccionadosList.length;
  const safePreviewIndex = totalSeleccionados > 0 ? Math.min(previewIndex, totalSeleccionados - 1) : 0;
  const productoPreview = seleccionadosList[safePreviewIndex] || null;
  const totalEtiquetas = seleccionadosList.reduce((sum, p) => sum + (p.cantidad * (config.copias || 1)), 0);

  // Preview
  // Escalar preview para que quepa en max ~500px de ancho o ~300px de alto
  const maxPreviewW = 500;
  const maxPreviewH = 300;
  const previewScale = Math.min(maxPreviewW / config.anchoMm, maxPreviewH / config.altoMm, 6);
  const previewW = config.anchoMm * previewScale;
  const previewH = config.altoMm * previewScale;

  const renderCanvasElements = () => {
    const precio = productoPreview
      ? productoPreview[config.precioMostrar] || productoPreview.precio1 || 0
      : 29.90;
    const codigoBarras = productoPreview
      ? (productoPreview.codigo_barras || productoPreview.codigo_corto || '7501234567890')
      : '7501234567890';
    const nombre = productoPreview
      ? (productoPreview.nombre || 'Producto ejemplo').substring(0, config.nombreMaxChars)
      : 'Producto ejemplo';

    const elems = config.elementos;
    const elements = [];

    const logoW = elems.logo?.anchoMm || 10;
    const logoH = elems.logo?.altoMm || 10;

    // Factor para convertir dots ZPL a pixels del preview: dots -> mm -> px
    const dotsPerMm = config.dpi / 25.4;
    const dotsToPx = (dots) => (dots / dotsPerMm) * previewScale;
    // Ajuste horizontal para que el ancho de texto CSS se acerque al de ZPL Font 0
    const fontScaleX = 1.12;

    // Tamaños por producto (override o global)
    const pNombreFs = getVal(productoPreview, 'nombreFontSize');
    const pPrecioFs = getVal(productoPreview, 'precioFontSize');
    const pCodigoTextoFs = getVal(productoPreview, 'codigoTextoFontSize');
    const pBarcodeHeight = getVal(productoPreview, 'barcodeHeight');
    const pBarcodeModuleWidth = getVal(productoPreview, 'barcodeModuleWidth');

    const elemDefs = {
      logo: {
        visible: elems.logo?.visible && config.logoImage,
        x: elems.logo?.x || 0,
        y: elems.logo?.y || 0,
        render: () => (
          <img
            src={config.logoImage}
            alt="Logo"
            draggable={false}
            style={{
              width: logoW * previewScale,
              height: logoH * previewScale,
              objectFit: 'contain',
              display: 'block',
              imageRendering: 'auto',
            }}
          />
        ),
      },
      nombre: {
        visible: elems.nombre?.visible,
        x: elems.nombre?.x || 0,
        y: elems.nombre?.y || 0,
        render: () => (
          <span style={{
            fontSize: Math.max(6, dotsToPx(pNombreFs) * 0.75),
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            transform: `scaleX(${fontScaleX})`,
            transformOrigin: 'left top',
            display: 'inline-block',
          }}>{nombre}</span>
        ),
      },
      codigoBarras: {
        visible: elems.codigoBarras?.visible,
        x: elems.codigoBarras?.x || 0,
        y: elems.codigoBarras?.y || 0,
        render: () => {
          const mw = Math.max(0.5, dotsToPx(pBarcodeModuleWidth));
          const bars = [];
          bars.push({ w: mw * 2, black: true }, { w: mw, black: false }, { w: mw, black: true }, { w: mw, black: false });
          codigoBarras.split('').forEach((char) => {
            const code = char.charCodeAt(0);
            const widths = [
              ((code * 3 + 7) % 4) + 1,
              ((code * 5 + 3) % 3) + 1,
              ((code * 7 + 11) % 4) + 1,
              ((code * 11 + 5) % 3) + 1,
              ((code * 13 + 2) % 3) + 1,
              ((code * 17 + 1) % 3) + 1,
            ];
            widths.forEach((w, j) => {
              bars.push({ w: mw * w, black: j % 2 === 0 });
            });
          });
          bars.push({ w: mw * 2, black: true }, { w: mw, black: false }, { w: mw * 2, black: true });

          return (
            <span className="preview-barcode-visual" style={{
              height: Math.max(10, dotsToPx(pBarcodeHeight)),
              display: 'inline-flex',
              alignItems: 'stretch',
            }}>
              {bars.map((b, i) => (
                <span key={i} style={{ width: b.w, background: b.black ? '#000' : '#fff', display: 'block' }} />
              ))}
            </span>
          );
        },
      },
      codigoTexto: {
        visible: elems.codigoTexto?.visible,
        x: elems.codigoTexto?.x || 0,
        y: elems.codigoTexto?.y || 0,
        render: () => (
          <span style={{
            fontSize: Math.max(5, dotsToPx(pCodigoTextoFs) * 0.75),
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            transform: `scaleX(${fontScaleX})`,
            transformOrigin: 'left top',
            display: 'inline-block',
          }}>{codigoBarras}</span>
        ),
      },
      precio: {
        visible: elems.precio?.visible,
        x: elems.precio?.x || 0,
        y: elems.precio?.y || 0,
        render: () => (
          <span style={{
            fontSize: Math.max(8, dotsToPx(pPrecioFs) * 0.75),
            fontWeight: 800,
            whiteSpace: 'nowrap',
            transform: `scaleX(${fontScaleX})`,
            transformOrigin: 'left top',
            display: 'inline-block',
          }}>{config.precioPrefix}{parseFloat(precio).toFixed(2)}</span>
        ),
      },
    };

    for (const [key, def] of Object.entries(elemDefs)) {
      if (!def.visible) continue;
      const isSelected = elemSeleccionado === key;
      const color = ELEM_COLORS[key];
      const sizeMap = sizeConfigMap[key];
      const isLogo = key === 'logo';
      const sizeVal = isLogo ? `${logoW}x${logoH}` : (sizeMap ? config[sizeMap.key] : null);
      const hasResize = isSelected && (sizeMap || isLogo);

      const isRightAligned = key === 'precio';
      elements.push(
        <div
          key={key}
          className={`canvas-element${isSelected ? ' selected' : ''}${isRightAligned ? ' right-aligned' : ''}`}
          style={{
            left: def.x * previewScale,
            top: def.y * previewScale,
            borderColor: isSelected ? color : 'transparent',
            '--elem-color': color,
          }}
          onMouseDown={(e) => handleElementMouseDown(e, key)}
          title={`${ELEM_LABELS[key]} (${def.x}, ${def.y})mm`}
        >
          {def.render()}
          {hasResize && (
            <div
              className="canvas-size-handle"
              style={{ '--elem-color': color }}
              onMouseDown={(e) => isLogo ? handleLogoResize(e) : handleSizeMouseDown(e, key)}
              title={isLogo ? `${logoW}x${logoH}mm — Arrastra para redimensionar` : `Alto: ${sizeVal} — Arrastra arriba/abajo`}
            >
              <span className="canvas-size-value">{sizeVal}</span>
            </div>
          )}
          {isSelected && key === 'codigoBarras' && (
            <div
              className="canvas-size-handle-right"
              style={{ '--elem-color': color }}
              onMouseDown={handleBarcodeWidthMouseDown}
              title={`Ancho mod: ${config.barcodeModuleWidth} — Arrastra izq/der`}
            >
              <span className="canvas-size-value">{config.barcodeModuleWidth}</span>
            </div>
          )}
          {isSelected && (
            <span className="canvas-elem-label" style={{ background: color }}>
              {ELEM_LABELS[key]} ({def.x},{def.y})
            </span>
          )}
        </div>
      );
    }
    return elements;
  };

  // Grid lines for preview
  const renderGrid = () => {
    if (!showGrid) return null;
    const lines = [];
    const stepMm = 5;
    for (let x = stepMm; x < config.anchoMm; x += stepMm) {
      lines.push(<div key={`vl${x}`} className="grid-line grid-line-v" style={{ left: x * previewScale }} />);
    }
    for (let y = stepMm; y < config.altoMm; y += stepMm) {
      lines.push(<div key={`hl${y}`} className="grid-line grid-line-h" style={{ top: y * previewScale }} />);
    }
    return lines;
  };

  return (
    <div className="etiquetas-zebra-page">
      <div className="page-header">
        <div className="perfiles-section">
          <select
            className="perfil-select"
            value={perfilActivo}
            onChange={(e) => {
              if (e.target.value) cargarPerfil(e.target.value);
              else { setPerfilActivo(''); }
            }}
          >
            <option value="">Sin perfil</option>
            {perfiles.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
          {perfilActivo && (
            <>
              <button className="btn-zb secondary small" onClick={() => duplicarPerfil(perfilActivo)} title="Duplicar perfil">
                <Copy size={14} />
              </button>
              <button className="btn-zb secondary small" onClick={() => eliminarPerfil(perfilActivo)} title="Eliminar perfil" style={{ color: '#ef4444' }}>
                <Trash2 size={14} />
              </button>
            </>
          )}
          {mostrarNuevoPerfil ? (
            <div className="nuevo-perfil-inline">
              <input
                type="text"
                placeholder="Nombre del perfil..."
                value={nombreNuevoPerfil}
                onChange={(e) => setNombreNuevoPerfil(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') crearPerfil(); if (e.key === 'Escape') setMostrarNuevoPerfil(false); }}
                autoFocus
              />
              <button className="btn-zb primary small" onClick={crearPerfil}><Save size={14} /></button>
              <button className="btn-zb secondary small" onClick={() => setMostrarNuevoPerfil(false)}><X size={14} /></button>
            </div>
          ) : (
            <button className="btn-zb secondary small" onClick={() => setMostrarNuevoPerfil(true)} title="Crear nuevo perfil">
              <Plus size={14} /> Nuevo perfil
            </button>
          )}
        </div>
        <div className="header-actions">
          <button className="btn-zb secondary small" onClick={resetConfig} title="Restaurar valores predeterminados">
            <RotateCcw size={14} /> Resetear
          </button>
          <button className="btn-zb secondary small" onClick={guardarConfigManual} disabled={guardando} title="Guardar configuracion en base de datos">
            {guardando ? <Loader size={14} className="spinning" /> : <Save size={14} />} {guardando ? 'Guardando...' : configGuardada ? 'Guardado!' : 'Guardar config'}
          </button>
          <button className="btn-zb secondary small" onClick={cargarImpresoras} disabled={loadingImpresoras}>
            <RefreshCw size={14} className={loadingImpresoras ? 'spinning' : ''} /> Impresoras
          </button>
        </div>
      </div>

      <div className="etiquetas-layout">
        {/* Panel izquierdo: Canvas + Config (2/3) */}
        <div className="etiquetas-config-panel">

          {/* Canvas de la etiqueta */}
          <div className="config-card preview-card">
            <div className="preview-header">
              <h3><Eye size={16} /> Diseño de etiqueta</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {totalSeleccionados > 0 && (
                  <div className="preview-nav">
                    <button className="btn-zb secondary small" disabled={safePreviewIndex <= 0}
                      onClick={() => setPreviewIndex(i => Math.max(0, i - 1))}>
                      <ChevronLeft size={14} />
                    </button>
                    <span className="preview-nav-label">{safePreviewIndex + 1}/{totalSeleccionados}</span>
                    <button className="btn-zb secondary small" disabled={safePreviewIndex >= totalSeleccionados - 1}
                      onClick={() => setPreviewIndex(i => Math.min(totalSeleccionados - 1, i + 1))}>
                      <ChevronRight size={14} />
                    </button>
                  </div>
                )}
                <label className="grid-toggle">
                  <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} />
                  <span>Cuadricula</span>
                </label>
              </div>
            </div>
            {productoPreview && (
              <div className="preview-product-info">
                <div className="preview-product-name" title={productoPreview.nombre}>
                  {productoPreview.nombre}
                </div>
                <div className="preview-product-overrides">
                  {[
                    { key: 'nombreFontSize', label: 'Nombre', min: 10, max: 999 },
                    { key: 'precioFontSize', label: 'Precio', min: 12, max: 999 },
                    { key: 'codigoTextoFontSize', label: 'Cod. texto', min: 8, max: 999 },
                    { key: 'barcodeHeight', label: 'Barcode H', min: 20, max: 999 },
                  ].map(f => (
                    <div key={f.key} className="override-field">
                      <label>{f.label}</label>
                      <NumInput
                        value={getVal(productoPreview, f.key)}
                        min={f.min} max={f.max}
                        onChange={(v) => updateProductoOverride(productoPreview.id, f.key, v)}
                        className={productoPreview.overrides?.[f.key] != null ? 'has-override' : ''}
                      />
                    </div>
                  ))}
                  {productoPreview.overrides && Object.keys(productoPreview.overrides).length > 0 && (
                    <button className="btn-zb secondary small" onClick={() => resetProductoOverrides(productoPreview.id)}
                      title="Usar valores globales">
                      <RotateCcw size={12} /> Reset
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="label-preview-wrapper">
              <div className="label-preview-outer" style={{ width: previewW + 2, height: previewH + 2 }}>
                <div
                  ref={labelRef}
                  className="label-preview-label label-canvas"
                  style={{ width: previewW, height: previewH }}
                  onMouseDown={(e) => { if (e.target === labelRef.current) setElemSeleccionado(null); }}
                >
                  {renderGrid()}
                  {renderCanvasElements()}
                </div>
              </div>
              <div className="preview-dimensions">
                {config.anchoMm} x {config.altoMm} mm | {config.dpi} dpi
                {elemSeleccionado && (
                  <span className="preview-selected-info" style={{ color: ELEM_COLORS[elemSeleccionado] }}>
                    {' | '}{ELEM_LABELS[elemSeleccionado]}: x={config.elementos[elemSeleccionado]?.x}mm y={config.elementos[elemSeleccionado]?.y}mm
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Configuracion */}
          <div className="config-2col">
            {/* Columna izquierda */}
            <div className="config-col">
              {/* Tamano de etiqueta */}
              <div className="config-card">
                <h3><Tag size={16} /> Etiqueta</h3>
                <div className="config-grid-2">
                  <div className="config-field">
                    <label>Ancho (mm)</label>
                    <NumInput value={config.anchoMm} min={10} max={9999}
                      onChange={(v) => updateConfig({ anchoMm: v })} />
                  </div>
                  <div className="config-field">
                    <label>Alto (mm)</label>
                    <NumInput value={config.altoMm} min={5} max={9999}
                      onChange={(v) => updateConfig({ altoMm: v })} />
                  </div>
                </div>
                <div className="preset-btns">
                  {[
                    { label: '50x25', w: 50, h: 25 },
                    { label: '50x30', w: 50, h: 30 },
                    { label: '60x40', w: 60, h: 40 },
                    { label: '75x50', w: 75, h: 50 },
                    { label: '100x50', w: 100, h: 50 },
                  ].map(p => (
                    <button key={p.label}
                      className={`preset-btn ${config.anchoMm === p.w && config.altoMm === p.h ? 'active' : ''}`}
                      onClick={() => updateConfig({ anchoMm: p.w, altoMm: p.h })}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Elementos: visibilidad y posicion */}
              <div className="config-card">
                <h3><Move size={16} /> Elementos</h3>
                <div className="elementos-canvas-list">
                  {Object.entries(config.elementos).map(([key, elem]) => (
                    <div
                      key={key}
                      className={`elemento-canvas-item${elemSeleccionado === key ? ' selected' : ''}`}
                      style={{ '--elem-color': ELEM_COLORS[key] }}
                      onClick={() => setElemSeleccionado(key)}
                    >
                      <input type="checkbox" checked={elem.visible}
                        disabled={key === 'logo' && !config.logoImage}
                        onChange={(e) => { e.stopPropagation(); updateElemento(key, { visible: e.target.checked }); }} />
                      <span className="elem-color-dot" style={{ background: ELEM_COLORS[key] }} />
                      <span className="elem-label">{ELEM_LABELS[key]}</span>
                      {elem.visible && (
                        <div className="elem-pos-inputs">
                          <label>x</label>
                          <NumInput value={elem.x} min={0} max={config.anchoMm} step={0.5} isFloat
                            onChange={(v) => updateElemento(key, { x: v })} />
                          <label>y</label>
                          <NumInput value={elem.y} min={0} max={config.altoMm} step={0.5} isFloat
                            onChange={(v) => updateElemento(key, { y: v })} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Logo */}
              <div className="config-card">
                <h3><Image size={16} /> Logo</h3>
                <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={handleLogoUpload} />
                {config.logoImage ? (
                  <div className="logo-preview-row">
                    <img src={config.logoImage} alt="Logo" className="logo-thumb" />
                    <div className="logo-actions">
                      <button className="btn-zb secondary small" onClick={() => logoInputRef.current?.click()}>
                        Cambiar
                      </button>
                      <button className="btn-zb secondary small" style={{ color: '#ef4444' }} onClick={quitarLogo}>
                        <X size={12} /> Quitar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="btn-zb secondary small" onClick={() => logoInputRef.current?.click()} style={{ width: '100%' }}>
                    <Image size={14} /> Subir logo
                  </button>
                )}
                {config.logoImage && config.elementos.logo?.visible && (
                  <div className="config-grid-2" style={{ marginTop: 8, marginBottom: 0 }}>
                    <div className="config-field">
                      <label>Ancho (mm)</label>
                      <NumInput value={config.elementos.logo?.anchoMm || 10} min={2} max={config.anchoMm} step={0.5} isFloat
                        onChange={(v) => updateElemento('logo', { anchoMm: v })} />
                    </div>
                    <div className="config-field">
                      <label>Alto (mm)</label>
                      <NumInput value={config.elementos.logo?.altoMm || 10} min={2} max={config.altoMm} step={0.5} isFloat
                        onChange={(v) => updateElemento('logo', { altoMm: v })} />
                    </div>
                  </div>
                )}
              </div>

              {/* Datos */}
              <div className="config-card">
                <h3>Datos</h3>
                <div className="config-row">
                  <label>Precio a mostrar</label>
                  <select value={config.precioMostrar}
                    onChange={(e) => updateConfig({ precioMostrar: e.target.value })}>
                    <option value="precio_lista">Precio Lista</option>
                    <option value="precio1">Precio 1</option>
                    <option value="precio2">Precio 2</option>
                    <option value="precio3">Precio 3</option>
                  </select>
                </div>
                <div className="config-row">
                  <label>Copias por producto</label>
                  <NumInput value={config.copias} min={1} max={999}
                    onChange={(v) => updateConfig({ copias: v })}
                    style={{ width: 60, minWidth: 60 }} />
                </div>
              </div>
            </div>

            {/* Columna derecha */}
            <div className="config-col">
              {/* Tipografia y tamanos */}
              <div className="config-card">
                <h3><Settings size={16} /> Tamanos</h3>
                {config.elementos.nombre?.visible && (
                  <>
                    <div className="config-row">
                      <label>Nombre (font)</label>
                      <NumInput value={config.nombreFontSize} min={10} max={999}
                        onChange={(v) => updateConfig({ nombreFontSize: v })} />
                    </div>
                    <div className="config-row">
                      <label>Max caracteres</label>
                      <NumInput value={config.nombreMaxChars} min={5} max={999}
                        onChange={(v) => updateConfig({ nombreMaxChars: v })} />
                    </div>
                  </>
                )}
                {config.elementos.precio?.visible && (
                  <>
                    <div className="config-row">
                      <label>Precio (font)</label>
                      <NumInput value={config.precioFontSize} min={12} max={999}
                        onChange={(v) => updateConfig({ precioFontSize: v })} />
                    </div>
                    <div className="config-row">
                      <label>Prefijo precio</label>
                      <input type="text" value={config.precioPrefix} maxLength={5} style={{ width: 60, minWidth: 60 }}
                        onChange={(e) => updateConfig({ precioPrefix: e.target.value })} />
                    </div>
                  </>
                )}
                {config.elementos.codigoTexto?.visible && (
                  <div className="config-row">
                    <label>Codigo texto (font)</label>
                    <NumInput value={config.codigoTextoFontSize} min={8} max={999}
                      onChange={(v) => updateConfig({ codigoTextoFontSize: v })} />
                  </div>
                )}
                {config.elementos.codigoBarras?.visible && (
                  <>
                    <div className="config-row">
                      <label>Barcode alto (dots)</label>
                      <NumInput value={config.barcodeHeight} min={20} max={999}
                        onChange={(v) => updateConfig({ barcodeHeight: v })} />
                    </div>
                    <div className="config-row">
                      <label>Barcode ancho mod.</label>
                      <NumInput value={config.barcodeModuleWidth} min={1} max={99}
                        onChange={(v) => updateConfig({ barcodeModuleWidth: v })} />
                    </div>
                    <div className="config-row">
                      <label>Tipo codigo</label>
                      <select value={config.barcodeType}
                        onChange={(e) => updateConfig({ barcodeType: e.target.value })}>
                        <option value="CODE128">Code 128</option>
                        <option value="EAN13">EAN-13</option>
                        <option value="CODE39">Code 39</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              {/* Impresora */}
              <div className="config-card">
                <h3><Printer size={16} /> Impresora</h3>
                <div className="config-row">
                  <label>Impresora</label>
                  <select value={config.impresora} disabled={loadingImpresoras}
                    onChange={(e) => updateConfig({ impresora: e.target.value })}>
                    <option value="">Seleccionar...</option>
                    {impresoras.map((p) => (
                      <option key={p.name || p} value={p.name || p}>
                        {p.displayName || p.name || p}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-row">
                  <label>DPI</label>
                  <select value={config.dpi}
                    onChange={(e) => updateConfig({ dpi: parseInt(e.target.value) })}>
                    <option value={203}>203 dpi</option>
                    <option value={300}>300 dpi</option>
                    <option value={600}>600 dpi</option>
                  </select>
                </div>
                <div className="config-row">
                  <label>Velocidad (1-14)</label>
                  <NumInput value={config.velocidad} min={1} max={14}
                    onChange={(v) => updateConfig({ velocidad: v })}
                    style={{ width: 60, minWidth: 60 }} />
                </div>
                <div className="config-row">
                  <label>Oscuridad (0-30)</label>
                  <NumInput value={config.oscuridad} min={0} max={30}
                    onChange={(v) => updateConfig({ oscuridad: v })}
                    style={{ width: 60, minWidth: 60 }} />
                </div>
              </div>
            </div>
          </div>

          {/* Boton imprimir */}
          <div className="action-buttons">
            <button className="btn-zb primary" onClick={imprimirEtiquetas}
              disabled={imprimiendo || Object.keys(seleccionados).length === 0}>
              {imprimiendo ? (
                <><Loader size={16} className="spinning" /> Imprimiendo...</>
              ) : (
                <><Printer size={16} /> Imprimir {totalEtiquetas > 0 ? `(${totalEtiquetas})` : ''}</>
              )}
            </button>
          </div>
        </div>

        {/* Panel derecho: Productos (1/3) */}
        <div className="etiquetas-productos-panel">
          <div className="panel-header">
            <div style={{ position: 'relative', flex: 1, minWidth: 120 }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                className="search-input"
                placeholder="Buscar producto..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={{ paddingLeft: 34 }}
              />
            </div>
            <select
              className="categoria-select"
              value={categoriaFiltro}
              onChange={(e) => setCategoriaFiltro(e.target.value)}
            >
              <option value="">Todas</option>
              {categorias.map(c => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          {productos.length > 0 && (
            <div className="select-all-row">
              <input
                type="checkbox"
                checked={productos.length > 0 && productos.every(p => seleccionados[p.id])}
                onChange={toggleTodos}
              />
              <span>Todos ({productos.length})</span>
              {Object.keys(seleccionados).length > 0 && (
                <button className="btn-clear-selection" onClick={() => setSeleccionados({})}>
                  Limpiar ({Object.keys(seleccionados).length})
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="loading-overlay">
              <Loader size={20} className="spinning" /> Cargando...
            </div>
          ) : (
            <div className="productos-table-container">
              <table className="productos-table">
                <thead>
                  <tr>
                    <th className="checkbox-cell"></th>
                    <th>Producto</th>
                    <th>Precio</th>
                    <th className="qty-cell">Cant.</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.length === 0 ? (
                    <tr><td colSpan="4" style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>No se encontraron productos</td></tr>
                  ) : (
                    productos.map(producto => {
                      const isSelected = !!seleccionados[producto.id];
                      return (
                        <tr key={producto.id} className={isSelected ? 'selected' : ''}>
                          <td className="checkbox-cell">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleProducto(producto)} />
                          </td>
                          <td><div className="product-name" title={producto.nombre}>{producto.nombre}</div></td>
                          <td><span className="product-price">${parseFloat(producto[config.precioMostrar] || producto.precio1 || 0).toFixed(2)}</span></td>
                          <td className="qty-cell">
                            {isSelected && (
                              <input type="number" className="qty-input" value={seleccionados[producto.id]?.cantidad || 1}
                                min="1" max="999" onChange={(e) => cambiarCantidad(producto.id, e.target.value)} />
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Resumen seleccion */}
          {Object.keys(seleccionados).length > 0 && (
            <div className="seleccion-resumen">
              <div className="resumen-header">
                <h4>{Object.keys(seleccionados).length} producto(s)</h4>
                <span className="total-etiquetas">{totalEtiquetas} etiqueta(s)</span>
              </div>
              <div className="seleccion-list">
                {seleccionadosList.map((p, idx) => (
                  <div key={p.id} className={`seleccion-item${idx === safePreviewIndex ? ' previewing' : ''}`}
                    onClick={() => setPreviewIndex(idx)} title="Click para ver preview">
                    <span className="item-name">{p.nombre}</span>
                    <span className="item-qty">x{p.cantidad * (config.copias || 1)}</span>
                    <button className="remove-btn" onClick={(e) => { e.stopPropagation(); quitarSeleccion(p.id); }}><X size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="table-footer">
            <span>{totalProductos} productos</span>
            <div className="pagination-btns">
              <button disabled={pagina <= 1} onClick={() => cargarProductos(pagina - 1)}><ChevronLeft size={14} /></button>
              <span style={{ padding: '4px 8px', fontSize: 12 }}>{pagina}/{totalPaginas}</span>
              <button disabled={pagina >= totalPaginas} onClick={() => cargarProductos(pagina + 1)}><ChevronRight size={14} /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EtiquetasZebraPage;
